// Folder notes are HTML from a contentEditable, and the server stores them as sent (it has no DOM to
// sanitise with). So every show and every save goes through cleanNotes: only what the toolbar makes.
//
//   kept      p div br · b strong i em u s strike · span with a colour, bold, italic, underline, strike
//             · ul li · a checklist <ul data-checklist="true">, a done item <li data-checked="true">
//   dropped   everything else: scripts, links, images, classes, ids, event handlers, any other style
import DOMPurify, { type Config } from 'dompurify';

const TAGS = ['p', 'div', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'span', 'ul', 'li'];

const COLOR = /^(#[0-9a-f]{3}([0-9a-f]{3})?|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\))$/i;
/** One or both lines ("underline line-through"); "none" undoes them inside a styled run. */
const DECORATION = /^(none|(underline|line-through)(\s+(underline|line-through))?)$/i;

/** The style properties execCommand writes (styleWithCSS), each with the only values it may hold. */
const STYLES: Record<string, RegExp> = {
  color: COLOR,
  'font-weight': /^(normal|bold|[1-9]00)$/i,
  'font-style': /^(normal|italic)$/i,
  'text-decoration': DECORATION,
  'text-decoration-line': DECORATION,
};

/** A style attribute with only the allowed declarations: "color: rgb(225, 29, 72); font-weight: bold". '' = none left. */
export function cleanStyle(style: string): string {
  return style
    .split(';')
    .map((d) => {
      const i = d.indexOf(':');
      if (i < 0) return '';
      const prop = d.slice(0, i).trim().toLowerCase();
      const value = d.slice(i + 1).trim();
      return Object.hasOwn(STYLES, prop) && STYLES[prop]!.test(value) ? `${prop}: ${value}` : '';
    })
    .filter(Boolean)
    .join('; ');
}

/**
 * An attribute's value as kept (`tag` lower-case), null = dropped. The data attributes only as exactly
 * "true" on their own element: any other value would still match [data-…] in the CSS.
 */
export function keptAttr(tag: string, name: string, value: string): string | null {
  if (name === 'style') return cleanStyle(value) || null;
  if (name === 'data-checklist') return tag === 'ul' && value === 'true' ? 'true' : null;
  if (name === 'data-checked') return tag === 'li' && value === 'true' ? 'true' : null;
  return null;
}

const CONFIG: Config = {
  ALLOWED_TAGS: TAGS,
  ALLOWED_ATTR: ['style', 'data-checklist', 'data-checked'],
  // Otherwise every data-* and aria-* attribute would pass.
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

let purifier: ReturnType<typeof DOMPurify> | null = null;

/** Our own instance: its hook does not reach any other use of DOMPurify. null without a DOM. */
function purify() {
  if (purifier) return purifier;
  if (typeof window === 'undefined') return null;
  const p = DOMPurify(window);
  if (!p.isSupported) return null;
  p.addHook('uponSanitizeAttribute', (node, data) => {
    const v = keptAttr(node.nodeName.toLowerCase(), data.attrName, data.attrValue);
    if (v === null) data.keepAttr = false;
    else data.attrValue = v;
  });
  purifier = p;
  return p;
}

/** The notes as they may be shown or saved. No DOM to clean them with (DOMPurify then returns its input): nothing. */
export function cleanNotes(html: string): string {
  if (!html) return '';
  const p = purify();
  return p ? String(p.sanitize(html, CONFIG)).trim() : '';
}
